import { Controller } from '@hotwired/stimulus';
import CurrentUser from '@frontend/app/features/CurrentUser';
import { get } from '@rails/request.js';
import { runWhenDomLoaded } from '@frontend/utils/dom';

/*
 * Useful to asynchronously load dynamic content.
 * Use cases:
 * - loading dynamic parts on a cached page, e.g. the article page
 * - load paginated content, e.g. article teasers.
 *
 * Build to be attached to placeholder tags (e.g. a `<div>`) or `<a>`-tags.
 *
 * == Examples
 *
 * <div data-controller="lazy-partial"></div> // The div will be replaced with the dynamically loaded content
 * <a href="/articles?page=2" data-controller="lazy-partial">Load more</a>
 *
 * */
export default class extends Controller {
  static values = {
    path: String,
    skipLoading: Boolean,
    requiredRoles: Array,
  };
  static targets = ['container'];

  connect() {
    this.element.addEventListener(
      'lazy-partial:reload',
      this.reload.bind(this)
    );
    const userRoles = CurrentUser.roles();
    const requiresRole = this.requiredRolesValue.length > 0;
    const userHasRequiredRole =
      this.requiredRolesValue.filter((role) => userRoles.includes(role))
        .length > 0;
    const skipLoading =
      this.skipLoadingValue || (requiresRole && !userHasRequiredRole);

    if (false === skipLoading) {
      runWhenDomLoaded(this.load.bind(this, true));
    }

    if (this.requiredRolesValue.length > 0 && false === userHasRequiredRole) {
      this.element.remove();
    }
  }

  reload(event) {
    event.preventDefault();

    // debugger;
    let path = undefined;
    if (event.params) {
      path = event.params.path;
    }
    const { lazyPartialPath } = event.currentTarget.dataset;
    if (lazyPartialPath || path) {
      this.pathValue = lazyPartialPath || path;
    }

    this.load(false);
  }

  scrollToAnchor(parentElement) {
    const targetElementID = window.location.hash;
    const targetElement =
      targetElementID.length > 0
        ? parentElement.querySelector(targetElementID)
        : false;

    if (targetElement) {
      targetElement.scrollIntoView();
    }
  }

  async load(scrollToAnchor = false) {
    const response = await get(this.pathValue);
    if (response.ok) {
      const html = await response.text;
      const parentElement = this.hasContainerTarget
        ? this.containerTarget
        : this.element;

      if (this.hasContainerTarget) {
        this.containerTarget.innerHTML = html;
      } else if (this.element.parentNode) {
        this.element.outerHTML = html;
      } else {
        return;
      }

      // If the current url references a valid anchor, we attemp to scroll to it. Useful to deep-link to parts
      // of dynamically loaded content, e.g. a comment in the comment system.
      if (scrollToAnchor) {
        this.scrollToAnchor(parentElement);
      }
    }
  }
}

export function reload(targetEl) {
  const dispatchTarget =
    targetEl.closest('[data-controller~="lazy-partial"]') || targetEl;

  dispatchTarget.dispatchEvent(
    new CustomEvent('lazy-partial:reload', { bubbles: false })
  );
}
